import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const EXAMPLE_URL = "https://arxiv.org/abs/1706.03762";

function authorLine(authors) {
  if (!authors?.length) return "";
  if (authors.length === 1) return authors[0];
  if (authors.length === 2) return `${authors[0]} and ${authors[1]}`;
  return `${authors.slice(0, -1).join(", ")}, and ${authors[authors.length - 1]}`;
}

function MediaBlock({ block }) {
  const caption = block.caption || "";
  if (block.src) {
    return (
      <Box
        component="figure"
        sx={{
          my: 3,
          mx: 0,
          p: 0,
        }}
      >
        <Box
          component="img"
          src={block.src}
          alt={caption || block.type}
          sx={{
            display: "block",
            width: "100%",
            maxWidth: "100%",
            height: "auto",
            borderRadius: "12px",
            border: "1px solid rgba(255,255,255,0.12)",
            background: "#fff",
          }}
        />
        {caption ? (
          <Typography
            component="figcaption"
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1.25, lineHeight: 1.6, fontStyle: "italic" }}
          >
            {caption}
          </Typography>
        ) : null}
      </Box>
    );
  }

  if (block.type === "table" && block.rows?.length) {
    const headers = block.headers || [];
    const colCount = Math.max(
      headers.length,
      ...block.rows.map((row) => row.length),
      1
    );
    return (
      <Box component="figure" sx={{ my: 3, mx: 0 }}>
        <Box sx={{ overflowX: "auto", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "12px" }}>
          <Box
            component="table"
            sx={{
              width: "100%",
              borderCollapse: "collapse",
              "& th, & td": {
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                px: 1.5,
                py: 1,
                textAlign: "left",
                fontSize: "0.92rem",
                verticalAlign: "top",
              },
              "& th": { color: "text.secondary", fontWeight: 600 },
            }}
          >
            {headers.length ? (
              <thead>
                <tr>
                  {Array.from({ length: colCount }, (_, i) => (
                    <th key={`h-${i}`}>{headers[i] || ""}</th>
                  ))}
                </tr>
              </thead>
            ) : null}
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={`r-${r}`}>
                  {Array.from({ length: colCount }, (_, i) => (
                    <td key={`c-${r}-${i}`}>{row[i] || ""}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
        {caption ? (
          <Typography
            component="figcaption"
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1.25, lineHeight: 1.6, fontStyle: "italic" }}
          >
            {caption}
          </Typography>
        ) : null}
      </Box>
    );
  }

  if (caption) {
    return (
      <Typography color="text.secondary" sx={{ my: 2, fontStyle: "italic", lineHeight: 1.7 }}>
        {caption}
      </Typography>
    );
  }
  return null;
}

function ArticleSection({ section }) {
  const isAbstract = section.kind === "abstract";
  const isRefs = section.kind === "references";
  const [open, setOpen] = useState(!isRefs);

  if (isRefs) {
    return (
      <Box sx={{ mt: 5 }}>
        <Button
          color="inherit"
          onClick={() => setOpen((value) => !value)}
          sx={{ px: 0, mb: 1, color: "text.secondary" }}
        >
          {open ? "Hide references" : `Show references (${section.paragraphs.length})`}
        </Button>
        <Collapse in={open}>
          <Box component="ol" sx={{ pl: 3, m: 0 }}>
            {section.paragraphs.map((item, index) => (
              <Typography
                key={`${index}-${item.slice(0, 24)}`}
                component="li"
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.25, lineHeight: 1.7 }}
              >
                {item}
              </Typography>
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: isAbstract ? 0 : 4 }}>
      {!isAbstract && (
        <Typography
          variant="h5"
          component="h2"
          sx={{
            mb: 2,
            fontWeight: 700,
            fontSize: { xs: "1.25rem", md: "1.4rem" },
            color: "text.primary",
          }}
        >
          {section.heading}
        </Typography>
      )}
      {isAbstract && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            mb: 1,
            bgcolor: "rgba(102, 126, 234, 0.08)",
            border: "1px solid rgba(102, 126, 234, 0.22)",
            borderRadius: "16px",
          }}
        >
          <Typography
            variant="overline"
            sx={{ color: "primary.main", letterSpacing: 1.2 }}
          >
            Abstract
          </Typography>
          {(section.blocks?.length
            ? section.blocks
            : (section.paragraphs || []).map((text) => ({ type: "paragraph", text }))
          ).map(
            (block, index) =>
              block.type === "paragraph" ? (
                <Typography
                  key={`${section.heading}-abs-${index}`}
                  sx={{ mt: 1, lineHeight: 1.8, color: "rgba(255,255,255,0.88)" }}
                >
                  {block.text}
                </Typography>
              ) : (
                <MediaBlock key={`${section.heading}-abs-media-${index}`} block={block} />
              )
          )}
        </Paper>
      )}
      {!isAbstract &&
        (section.blocks?.length
          ? section.blocks
          : (section.paragraphs || []).map((text) => ({ type: "paragraph", text }))
        ).map(
          (block, index) =>
            block.type === "paragraph" ? (
              <Typography
                key={`${section.heading}-${index}`}
                paragraph
                sx={{
                  lineHeight: 1.85,
                  fontSize: { xs: "1.02rem", md: "1.08rem" },
                  color: "rgba(255,255,255,0.88)",
                  mb: 2,
                }}
              >
                {block.text}
              </Typography>
            ) : (
              <MediaBlock key={`${section.heading}-media-${index}`} block={block} />
            )
        )}
    </Box>
  );
}

export default function PaperToBlogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [url, setUrl] = useState(searchParams.get("url") || "");
  const [article, setArticle] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const convert = async (rawUrl) => {
    const nextUrl = (rawUrl || "").trim();
    if (!nextUrl) {
      setError("Paste a research paper PDF link first.");
      return;
    }

    setLoading(true);
    setError("");
    setArticle(null);

    try {
      const response = await fetch("/api/paper-to-blog", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: nextUrl }),
      });
      const text = await response.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new Error(
          text.trimStart().startsWith("<!DOCTYPE") || text.trimStart().startsWith("<html")
            ? "Got HTML instead of JSON — use npm start so the local API is running."
            : "The converter returned an unexpected response."
        );
      }
      if (!response.ok) {
        throw new Error(payload.error || "Conversion failed.");
      }
      setArticle(payload);
      if (searchParams.get("url") !== nextUrl) {
        setSearchParams({ url: nextUrl }, { replace: true });
      }
    } catch (err) {
      setError(err.message || "Could not convert that paper.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initial = searchParams.get("url");
    if (initial && initial.trim()) {
      convert(initial);
    }
    // Run once on mount for shared links.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ width: "100%", maxWidth: 820, mx: "auto" }}>
      <Typography variant="h4" component="h1">
        Paper to blog
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
        Paste a public PDF link (arXiv abs/pdf links work). The paper is downloaded
        and shown as a single-column article, including figures and tables when
        they can be extracted. This page is not in the site navigation.
      </Typography>

      <Stack
        component="form"
        spacing={1.5}
        onSubmit={(event) => {
          event.preventDefault();
          convert(url);
        }}
      >
        <TextField
          fullWidth
          label="Research paper PDF URL"
          placeholder={EXAMPLE_URL}
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          disabled={loading}
          autoComplete="off"
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center">
          <Button type="submit" variant="contained" disabled={loading} sx={{ px: 3 }}>
            {loading ? "Converting…" : "Convert to blog"}
          </Button>
          <Chip
            label="Try Attention Is All You Need"
            clickable
            disabled={loading}
            onClick={() => {
              setUrl(EXAMPLE_URL);
              convert(EXAMPLE_URL);
            }}
            sx={{
              bgcolor: "rgba(102, 126, 234, 0.12)",
              border: "1px solid rgba(102, 126, 234, 0.3)",
            }}
          />
        </Stack>
      </Stack>

      {loading && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 4 }}>
          <CircularProgress size={22} />
          <Typography color="text.secondary">
            Downloading the PDF and formatting figures, tables, and text…
          </Typography>
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}

      {article && (
        <Box component="article" sx={{ mt: 5 }}>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontWeight: 750,
              fontSize: { xs: "1.7rem", md: "2.15rem" },
              lineHeight: 1.25,
              mb: 2,
              color: "text.primary",
            }}
          >
            {article.title}
          </Typography>
          {article.authors?.length ? (
            <Typography color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
              {authorLine(article.authors)}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
            <Chip size="small" label={`${article.readingMinutes} min read`} />
            {article.pageCount ? (
              <Chip size="small" label={`${article.pageCount} PDF pages`} />
            ) : null}
            {article.twoColumn ? (
              <Chip size="small" label="Converted from 2-column PDF" />
            ) : null}
            {article.sourceUrl ? (
              <Chip
                size="small"
                label="Original PDF"
                component="a"
                href={article.sourceUrl}
                clickable
                target="_blank"
                rel="noopener noreferrer"
              />
            ) : null}
          </Stack>
          {article.truncated ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              Only the first 40 pages were converted.
            </Alert>
          ) : null}
          <Divider sx={{ mb: 3 }} />
          {article.sections.map((section, index) => (
            <ArticleSection key={`${section.heading}-${index}`} section={section} />
          ))}
        </Box>
      )}
    </Box>
  );
}
